/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { checkEnvironment } from '@saviynt/common';
import { Cookies } from 'react-cookie';

import { ERROR_MESSAGE } from '../../utils/constants';

// Helper function to extract error messages with type safety
const getErrorMessage = (error) => {
  if (error && typeof error === 'object' && 'errorMessage' in error) {
    const { errorMessage } = error;

    if (typeof errorMessage === 'string') {
      return errorMessage;
    }
  }

  return ERROR_MESSAGE;
};

/**
 * HttpService is a utility class for making HTTP requests.
 * It uses the Fetch API under the hood and provides methods
 * for setting, deleting, and resetting headers.
 *
 * This class is exported as both a singleton instance and a class.
 * You can import the singleton instance for shared use across the
 * application, or import the class to create a new instance.
 * For simple http calls, it's recommended to use the singleton instance.
 *
 * @example
 * // Importing the singleton instance:
 * import HttpServiceSingleton from 'HttpService';
 * When using the singleton, do not modify headers as this can affect other
 * calls that are using the shared instance while your modified call is in flight.
 *
 * // Importing the class to create a new instance:
 * import { HttpService } from 'HttpService';
 * const httpServiceObj = new HttpService();
 * httpServiceObj.deleteHeader('Content-Type');
 *
 * @export
 * @class HttpService
 */
export class HttpService {
  constructor() {
    this.cookies = new Cookies();
    this.initialHeaders = () => ({
      'Content-Type': 'application/json',
      ...(checkEnvironment().FEATURE_HTTPONLY_ACCESSTOKEN ?? {
        Authorization: `${this.cookies.get('token_type')} ${this.cookies.get(
          'access_token'
        )}`,
      }),
    });
  }

  setHeaders(headers) {
    this.headers = {
      ...(this.headers || this.initialHeaders()),
      ...headers,
    };
  }

  deleteHeader(headerName) {
    this.headers = this.headers || this.initialHeaders();

    if (this.headers && headerName && typeof headerName === 'string') {
      const { [headerName]: removed, ...remainingHeaders } = this.headers;

      this.headers = remainingHeaders;
    }
  }

  resetHeaders() {
    this.headers = this.initialHeaders();
  }

  get(url, options = {}) {
    const { shouldThrowOnError = false } = options;

    return fetch(url, {
      method: 'GET',
      headers: this.headers || this.initialHeaders(),
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type');
        let responseData;

        // Parse response body based on content type
        if (contentType?.includes('application/json')) {
          responseData = await res.json();
        } else {
          responseData = await res.text();
        }

        // Check for success (2xx status codes)
        if (res.ok) {
          return responseData;
        }

        // Handle error responses (4xx, 5xx status codes)
        if (shouldThrowOnError) {
          // New behavior: throw proper error for try/catch handling
          let errorMessage = `HTTP ${res.status}`;

          if (
            responseData &&
            typeof responseData === 'object' &&
            responseData.message
          ) {
            errorMessage = responseData.message;
          } else if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else {
            const fallbackMessage = getErrorMessage(responseData);

            if (fallbackMessage) {
              errorMessage = fallbackMessage;
            }
          }

          const error = new Error(errorMessage);

          error.status = res.status;
          error.response = responseData;
          error.url = url;
          throw error;
        } else {
          // Backward compatible behavior: return Response object with error properties
          const error = responseData;
          const response = res;

          response.error = error;
          response.errorMessage = getErrorMessage(error);

          return response;
        }
      })
      .finally(() => {
        this.resetHeaders();
      });
  }

  post(url, req, errorCallback, shouldStringifyBody = true, options = {}) {
    const { shouldThrowOnError = false } = options;
    const body = shouldStringifyBody ? JSON.stringify(req) : req;

    return fetch(url, {
      method: 'POST',
      headers: this.headers || this.initialHeaders(),
      body,
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type');
        let responseData;

        // Parse response body based on content type
        if (contentType?.includes('application/json')) {
          responseData = await res.json();
        } else {
          responseData = await res.text();
        }

        // Check for success (2xx status codes: 200, 201, 204, etc.)
        if (res.ok) {
          return responseData;
        }

        // Handle error responses (4xx, 5xx status codes)
        if (shouldThrowOnError) {
          // New behavior: throw proper error for try/catch handling
          let errorMessage = `HTTP ${res.status}`;

          if (
            responseData &&
            typeof responseData === 'object' &&
            responseData.message
          ) {
            errorMessage = responseData.message;
          } else if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else {
            const fallbackMessage = getErrorMessage(responseData);

            if (fallbackMessage) {
              errorMessage = fallbackMessage;
            }
          }

          const error = new Error(errorMessage);

          error.status = res.status;
          error.response = responseData;
          error.url = url;
          throw error;
        } else {
          // Backward compatible behavior: return Response object with error properties
          const error = responseData;
          const response = res;

          response.error = error;
          response.errorMessage = getErrorMessage(error);

          if (errorCallback && typeof errorCallback === 'function') {
            errorCallback();
          }

          return response;
        }
      })
      .finally(() => {
        this.resetHeaders();
      });
  }

  put(url, req, errorCallback, shouldStringifyBody = true, options = {}) {
    const { shouldThrowOnError = false } = options;
    const body = shouldStringifyBody ? JSON.stringify(req) : req;

    return fetch(url, {
      method: 'PUT',
      headers: this.headers || this.initialHeaders(),
      body,
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type');
        let responseData;

        // Parse response body based on content type
        if (contentType?.includes('application/json')) {
          responseData = await res.json();
        } else {
          responseData = await res.text();
        }

        // Check for success (2xx status codes: 200, 201, 204, etc.)
        if (res.ok) {
          return responseData;
        }

        // Handle error responses (4xx, 5xx status codes)
        if (shouldThrowOnError) {
          // New behavior: throw proper error for try/catch handling
          let errorMessage = `HTTP ${res.status}`;

          if (
            responseData &&
            typeof responseData === 'object' &&
            responseData.message
          ) {
            errorMessage = responseData.message;
          } else if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else {
            const fallbackMessage = getErrorMessage(responseData);

            if (fallbackMessage) {
              errorMessage = fallbackMessage;
            }
          }

          const error = new Error(errorMessage);

          error.status = res.status;
          error.response = responseData;
          error.url = url;
          throw error;
        } else {
          // Backward compatible behavior: return Response object with error properties
          const error = responseData;
          const response = res;

          response.error = error;
          response.errorMessage = getErrorMessage(error);

          if (errorCallback && typeof errorCallback === 'function') {
            errorCallback();
          }

          return response;
        }
      })
      .finally(() => {
        this.resetHeaders();
      });
  }

  delete(url, req, errorCallback, shouldStringifyBody = true, options = {}) {
    const { shouldThrowOnError = false } = options;
    const body = shouldStringifyBody ? JSON.stringify(req) : req;

    // TODO: Enhance this when needed!
    return fetch(url, {
      method: 'DELETE',
      headers: this.headers || this.initialHeaders(),
      body,
    })
      .then(async (res) => {
        const contentType = res.headers.get('content-type');
        let responseData;

        // Parse response body based on content type (DELETE often returns 204 No Content)
        if (contentType?.includes('application/json')) {
          responseData = await res.json();
        } else {
          responseData = await res.text();
        }

        // Check for success (2xx status codes: 200, 204 No Content, etc.)
        if (res.ok) {
          return responseData;
        }

        // Handle error responses (4xx, 5xx status codes)
        if (shouldThrowOnError) {
          // New behavior: throw proper error for try/catch handling
          let errorMessage = `HTTP ${res.status}`;

          if (
            responseData &&
            typeof responseData === 'object' &&
            responseData.message
          ) {
            errorMessage = responseData.message;
          } else if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else {
            const fallbackMessage = getErrorMessage(responseData);

            if (fallbackMessage) {
              errorMessage = fallbackMessage;
            }
          }

          const error = new Error(errorMessage);

          error.status = res.status;
          error.response = responseData;
          error.url = url;
          throw error;
        } else {
          // Backward compatible behavior: return Response object with error properties
          const error = responseData;
          const response = res;

          response.error = error;
          response.errorMessage = getErrorMessage(error);

          if (errorCallback && typeof errorCallback === 'function') {
            errorCallback();
          }

          return response;
        }
      })
      .finally(() => {
        this.resetHeaders();
      });
  }
}

export default new HttpService();
